import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import type { VizEditorProps } from "./registry.js";
import { EmbedContext, ModalChromeContext } from "../markdown/EmbedFrame.js";
import { refOf, resolveRefPath } from "../markdown/parseFence.js";
import { useTree } from "../data/usePage.js";
import { fetchRaw, saveContent } from "../api.js";
import { invalidate } from "../data/useFetch.js";
import { fuzzyRank, matchSegments, type FuzzyMatch } from "../../shared/fuzzy.js";

/** Suggest a query filename slug from the SQL's first meaningful words. */
function suggestSlug(sql: string): string {
  const skip = new Set([
    "select", "from", "where", "group", "by", "order", "with",
    "as", "and", "or", "on", "join", "limit", "the", "a",
  ]);
  const words = (sql.replace(/--[^\n]*/g, " ").toLowerCase().match(/[a-z0-9_]+/g) ?? [])
    .filter((w) => !skip.has(w) && w.length > 1)
    .slice(0, 4);
  return (words.length ? words.join("-") : "query").slice(0, 40);
}

/**
 * Editor for SQL-kind embeds (query/metric/table/bar/line/waterfall/funnel).
 * Branches on whether the fence body is a bare `@ref` — a *reference* embed:
 *
 *  - reference  → `Query: [picker]  edit →`. The picker repoints the `@ref`;
 *                 "edit →" drills into the referenced `.sql` file's SQL right
 *                 here in the modal (QueryFileEditor), the header swapping to a
 *                 back button + the path. The page itself keeps no SQL textarea:
 *                 the SQL lives in the `.sql` file; `@ref` is an identity.
 *  - inline SQL → the raw SQL textarea, plus "move to .sql file" to migrate it.
 *
 * Attrs edit the same way in both. Per-viz param controls (series/axis/format…)
 * are the next layer and slot in between the query source and the raw attrs.
 */
export function SqlEmbedEditor({ attrs, source, updateCode }: VizEditorProps) {
  const { repo, path } = useContext(EmbedContext);
  const chrome = useContext(ModalChromeContext);
  const [pairs, setPairs] = useState<[string, string][]>(() => Object.entries(attrs));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Drill-in: edit the referenced .sql file's SQL right here in the modal.
  const [editingQuery, setEditingQuery] = useState(false);

  const commit = (next: [string, string][], body = source): void => {
    const obj: Record<string, string> = {};
    for (const [k, v] of next) if (k) obj[k] = v;
    updateCode(body, obj);
  };
  const setPair = (i: number, k: string, v: string): void => {
    const next = pairs.map((p, j) => (j === i ? ([k, v] as [string, string]) : p));
    setPairs(next);
    commit(next);
  };
  const addPair = (): void => setPairs([...pairs, ["", ""]]);
  const removePair = (i: number): void => {
    const next = pairs.filter((_, j) => j !== i);
    setPairs(next);
    commit(next);
  };

  const ref = refOf(source);
  const tree = useTree(repo);
  const sqlFiles = useMemo(
    () => (tree.status === "ready" ? tree.data.files.filter((f) => f.endsWith(".sql")) : []),
    [tree],
  );
  const resolvedPath =
    ref !== null ? resolveRefPath(ref, path || null, (p) => sqlFiles.includes(p)) : null;

  const pickQuery = (p: string): void => commit(pairs, "@" + p.replace(/\.sql$/, ""));

  const extractToFile = async (): Promise<void> => {
    const to = window.prompt(
      "Move this query to a reusable .sql file (repo path):",
      `queries/${suggestSlug(source)}.sql`,
    );
    if (!to) return;
    const p = to.endsWith(".sql") ? to : `${to}.sql`;
    setBusy(true);
    setError(null);
    try {
      await saveContent(repo, p, source.trim() + "\n");
      invalidate("tree"); // the picker + ref resolver see the new file
      commit(pairs, "@" + p.replace(/\.sql$/, ""));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // In query-edit mode, take over the modal header with a back button + path.
  const editingHere = editingQuery && resolvedPath !== null;
  useEffect(() => {
    if (!editingHere) return;
    chrome.setHead(
      <span className="dw-modal-back">
        <button
          type="button"
          className="dw-link dw-modal-back-btn"
          aria-label="Back to embed"
          onClick={() => setEditingQuery(false)}
        >
          ←
        </button>
        <code>{resolvedPath}</code>
      </span>,
    );
    return () => chrome.setHead(null);
  }, [editingHere, resolvedPath, chrome]);

  if (editingHere && resolvedPath) {
    return <QueryFileEditor repo={repo} path={resolvedPath} />;
  }

  return (
    <div className="dw-sql-editor">
      {ref !== null ? (
        <div className="dw-sql-ref">
          <label className="dw-muted dw-picker-label">Query</label>
          <QueryPicker files={sqlFiles} current={resolvedPath} onPick={pickQuery} />
          {resolvedPath ? (
            <button className="dw-link" onClick={() => setEditingQuery(true)}>
              edit →
            </button>
          ) : (
            <em className="dw-muted">unresolved</em>
          )}
        </div>
      ) : (
        <div className="dw-sql-inline">
          <textarea
            className="dw-textarea dw-fence-body"
            value={source}
            spellCheck={false}
            autoFocus
            onChange={(e) => commit(pairs, e.target.value)}
          />
          <button
            className="dw-link"
            disabled={busy}
            onClick={() => void extractToFile()}
            title="Extract this SQL into a reusable .sql file and reference it"
          >
            {busy ? "moving…" : "move to .sql file →"}
          </button>
          {error && <p className="dw-error">{error}</p>}
        </div>
      )}

      <table className="dw-attr-table">
        <tbody>
          {pairs.map(([k, v], i) => (
            <tr key={i}>
              <td>
                <input
                  className="dw-attr-key"
                  value={k}
                  placeholder="key"
                  spellCheck={false}
                  onChange={(e) => setPair(i, e.target.value, v)}
                />
              </td>
              <td>
                <input
                  className="dw-attr-val"
                  value={v}
                  placeholder="value"
                  spellCheck={false}
                  onChange={(e) => setPair(i, k, e.target.value)}
                />
              </td>
              <td>
                <button className="dw-link" onClick={() => removePair(i)}>
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="dw-link" onClick={addPair}>
        + attr
      </button>
    </div>
  );
}

/**
 * The referenced `.sql` file's SQL, edited inline in the embed modal (reached
 * via "edit →"). Loads the file's raw content, edits it in a textarea, and saves
 * back — invalidating query results so the embed re-runs against the new SQL.
 */
function QueryFileEditor({ repo, path }: { repo: string; path: string }) {
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setDraft(null);
    setError(null);
    fetchRaw(repo, path)
      .then((r) => live && setDraft(r.content))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [repo, path]);

  const save = async (): Promise<void> => {
    if (draft === null) return;
    setSaving(true);
    setError(null);
    try {
      await saveContent(repo, path, draft);
      // A query file changed — mirror the page editor's post-write invalidation
      // so the tree, commit state, and (crucially) cached query results refresh.
      invalidate("tree");
      invalidate("commit");
      invalidate("session");
      invalidate("query:");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dw-sql-file-editor">
      <textarea
        className="dw-textarea dw-fence-body"
        value={draft ?? ""}
        placeholder={draft === null && !error ? "loading…" : "select …"}
        spellCheck={false}
        autoFocus
        disabled={draft === null}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="dw-query-actions">
        <button className="primary" disabled={saving || draft === null} onClick={() => void save()}>
          {saving ? "saving…" : "save"}
        </button>
      </div>
      {error && <pre className="dw-error">{error}</pre>}
    </div>
  );
}

/** A directory (children, no `match`) or a query leaf (a `match`, no children)
 *  in the picker's pruned tree. */
interface QueryNode {
  name: string;
  path: string;
  match: FuzzyMatch<string> | null;
  children: QueryNode[];
}

/** Fold ranked `.sql` matches into a nested tree, keeping only the branches
 *  that lead to a match. Dirs sort before files, each alphabetically. */
function buildQueryTree(matches: FuzzyMatch<string>[]): QueryNode[] {
  const root: QueryNode = { name: "", path: "", match: null, children: [] };
  for (const m of matches) {
    const parts = m.item.split("/");
    let node = root;
    let prefix = "";
    parts.forEach((part, i) => {
      prefix = prefix ? `${prefix}/${part}` : part;
      const leaf = i === parts.length - 1;
      let child = node.children.find((c) => c.name === part && (c.match === null) !== leaf);
      if (!child) {
        child = { name: part, path: prefix, match: leaf ? m : null, children: [] };
        node.children.push(child);
      }
      node = child;
    });
  }
  const sort = (nodes: QueryNode[]): void => {
    nodes.sort((a, b) => {
      const dirDelta = Number(a.match !== null) - Number(b.match !== null);
      return dirDelta || a.name.localeCompare(b.name);
    });
    for (const n of nodes) sort(n.children);
  };
  sort(root.children);
  return root.children;
}

/** Leaf paths in render (top-to-bottom) order — the list the keyboard cursor
 *  walks. Mirrors QueryBranch's traversal (children are already sorted). */
function flattenLeaves(nodes: QueryNode[], out: string[] = []): string[] {
  for (const n of nodes) {
    if (n.match) out.push(n.path);
    else flattenLeaves(n.children, out);
  }
  return out;
}

/** Highlight the matched characters within a query leaf's basename. The fuzzy
 *  match is over the full path, so shift its offsets into the basename slice. */
function highlightBasename(match: FuzzyMatch<string>): ReactNode {
  const name = match.target.slice(match.target.lastIndexOf("/") + 1);
  const start = match.target.length - name.length;
  const segs = matchSegments(name, match.indexes.map((i) => i - start));
  return segs.map((s, i) => (s.hit ? <mark key={i}>{s.text}</mark> : s.text));
}

/**
 * Focus-to-tree query picker. The field reads like the current ref; on focus it
 * floats a panel showing the repo's `.sql` queries as a tree. Typing
 * fuzzy-filters the tree (pruned to matching branches, matches highlighted).
 *
 * An *active* cursor moves over the leaves: it starts on the current query (no
 * search) or the first match (searching), and follows ↑/↓ and the mouse. Enter
 * selects it, splicing `@<path-without-.sql>` (a repo-root path resolves via the
 * server's walk-up); Escape closes.
 */
function QueryPicker({
  files,
  current,
  onPick,
}: {
  files: string[];
  current: string | null;
  onPick: (path: string) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  // Where to pin the floating panel. It's portalled to <body> so the modal's
  // `overflow: auto` can't clip it, which means it must be positioned by hand.
  const [box, setBox] = useState<{ left: number; top: number; width: number } | null>(null);

  // Match against the path *without* `.sql` (cleaner leaf labels; the extension
  // can't absorb fuzzy chars). `item` stays the real path — it's the tree's
  // identity for current-highlight and onPick. The 0.5 threshold prunes weak
  // scattered hits (real matches score ~0.7+, noise ~0.4-).
  const tree = useMemo(
    () =>
      buildQueryTree(
        fuzzyRank(q, files, (f) => f.replace(/\.sql$/, ""), { limit: 200, threshold: 0.5 }),
      ),
    [files, q],
  );
  const leaves = useMemo(() => flattenLeaves(tree), [tree]);
  const activePath = leaves[active] ?? null;

  // Reset the cursor: current query when idle, first match when searching.
  useEffect(() => {
    if (!open) return;
    const i = q.trim() ? 0 : leaves.indexOf(current ?? "");
    setActive(i >= 0 ? i : 0);
  }, [open, q, leaves, current]);

  // Keep the panel pinned under the input while open (survives modal scroll/resize).
  useLayoutEffect(() => {
    if (!open) {
      setBox(null);
      return;
    }
    const place = (): void => {
      const r = inputRef.current?.getBoundingClientRect();
      if (r) setBox({ left: r.left, top: r.bottom + 2, width: r.width });
    };
    place();
    window.addEventListener("scroll", place, true); // capture: also catch modal scroll
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  // Keep the active leaf in view as the cursor moves (and when the panel opens).
  const shown = box !== null;
  useEffect(() => {
    if (open && shown) activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [open, shown, active]);

  const pick = (path: string): void => {
    onPick(path);
    setQ("");
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActive((a) => Math.min(a + 1, leaves.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      if (open && activePath) {
        e.preventDefault();
        pick(activePath);
      }
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    }
  };

  return (
    <div className="dw-query-picker">
      <input
        ref={inputRef}
        className="dw-query-picker-input"
        placeholder={current ? current.replace(/\.sql$/, "") : "search queries…"}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        onFocus={(e) => {
          e.target.select();
          setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open &&
        box &&
        createPortal(
          <div
            className="dw-query-picker-list"
            style={{ left: box.left, top: box.top, width: box.width }}
            // keep focus in the input so the blur-close logic still governs
            onMouseDown={(e) => e.preventDefault()}
          >
            {tree.length === 0 ? (
              <p className="dw-muted dw-query-picker-empty">
                {files.length === 0 ? "no .sql queries" : "no match"}
              </p>
            ) : (
              <QueryBranch
                nodes={tree}
                current={current}
                activePath={activePath}
                activeRef={activeRef}
                onHover={(p) => setActive(leaves.indexOf(p))}
                onPick={pick}
              />
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

/** One level of the picker tree: dirs render a label + nested branch, leaves a
 *  pick button. Recurses; every level shares the one `activeRef` (the cursor). */
function QueryBranch({
  nodes,
  current,
  activePath,
  activeRef,
  onHover,
  onPick,
}: {
  nodes: QueryNode[];
  current: string | null;
  activePath: string | null;
  activeRef: React.RefObject<HTMLButtonElement>;
  onHover: (path: string) => void;
  onPick: (path: string) => void;
}) {
  return (
    <ul className="dw-query-tree">
      {nodes.map((node) =>
        node.match ? (
          <li key={node.path}>
            <button
              type="button"
              ref={node.path === activePath ? activeRef : undefined}
              className={
                [
                  node.path === current ? "dw-picker-current" : "",
                  node.path === activePath ? "dw-picker-active" : "",
                ]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
              onMouseMove={() => onHover(node.path)}
              // mousedown fires before the input's blur closes the panel
              onMouseDown={(e) => {
                e.preventDefault();
                onPick(node.path);
              }}
            >
              {highlightBasename(node.match)}
            </button>
          </li>
        ) : (
          <li key={node.path} className="dw-query-tree-dir">
            <span className="dw-query-tree-dirname">{node.name}/</span>
            <QueryBranch
              nodes={node.children}
              current={current}
              activePath={activePath}
              activeRef={activeRef}
              onHover={onHover}
              onPick={onPick}
            />
          </li>
        ),
      )}
    </ul>
  );
}
